import React, { createContext, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  errorAlert,
  sweetalertLogout,
  successAlert,
  sweetalertConfirm,
} from '../helpers/alerts/sweetAlerts';
// API
import { getProfile, getProfileWithOptions, RequestLogin } from '../api/Auth';
import { RemoveUser } from '../api/Users';
import { setLocalRoute } from '../helpers/HelpersFunctions';
import { parsePermissionSlugs } from '../utils/permissions';
import { mapEligiblePanelApiToEligiblePanel } from '../utils/panels';
import { clearPendingLogin, savePendingLogin } from '../utils/pendingLogin';
import { resolveLegalVerificationStatus } from '../components/verification/legalVerificationStatus';
import {
  readStoredProfileSession,
  shouldApplyProfileResponse,
} from '../utils/profileResponse';
import { resetCleverTapIdentity } from '../utils/clevertap';
import { SessionRenewalModal } from '../components/modals/SessionRenewalModal';
import { SUSPENDED_PANEL_ERROR_CODE } from '../helpers/Errors';
const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({});
  let navigate = useNavigate();
  let location = useLocation();

  const [datalogin, setDataLogin] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dataLogin'));
    } catch {
      return null;
    }
  });
  // `datalogin` arranca con el snapshot de `localStorage` —hace falta para que
  // `Router.jsx` sepa desde el primer render si monta rutas privadas o
  // públicas—, pero esos permisos pueden estar desactualizados (revocados
  // desde el último login). Antes de este seed sincrónico, `datalogin`
  // arrancaba en `null` y el guard de permisos fallaba CERRADO —sin permisos—
  // hasta que `_isAuth` resolvía; con el seed puede fallar ABIERTO con
  // permisos viejos por el primer render. `ProtectedRoute` usa este flag para
  // esperar a que `getProfile` confirme los permisos antes de decidir.
  const [isProfileValidated, setIsProfileValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  let [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    _isAuth();
  }, []);

  const getUserData = async (currentRoute) => {
    const res = await getProfile();
    if (!res?.user) {
      return;
    }
    handleUserData(res);
    setLocalRoute(currentRoute);
  };

  // Vuelve a pedir `get-profile` sin cambiar de ruta. Lo usa la verificación
  // del panel (CORP-599) para ver el estado que dejó el webhook de Legal; con
  // `{ throwServerErrors: true }` un 5xx se lanza en vez de alertar. Una
  // respuesta que llega después de cambiar de panel, de cerrar sesión o de que
  // entre otro admin del mismo panel es de otra sesión y se descarta: el
  // polling de la activación agranda esa ventana. Se compara con
  // `localStorage` y no con `datalogin`, que en este cierre puede ser viejo.
  const refreshProfile = async (profileOptions) => {
    const requested = readStoredProfileSession(window.localStorage);
    const res = await getProfileWithOptions(profileOptions);
    const current = readStoredProfileSession(window.localStorage);
    if (!res?.user || !shouldApplyProfileResponse(requested, current)) {
      return;
    }
    // La sección la guarda el menú solo en `localStorage`: se lee al llegar la
    // respuesta para no pisarla con la de `datalogin`, que puede ser vieja.
    handleUserData(res, current.route ?? 'home');
  };

  const handleUserData = (res, route = 'home') => {
    const dataLogin = {
      route,
      email: res.user.email,
      fiscal_registry: res.user?.fiscal_registry,
      dni: res.user?.dni,
      name: res.user.name,
      address: res.user.address,
      country_id: res.user.country_id,
      country_name: res.user.country_name,
      country_phone_code: res.user.country_phone_code,
      phone: res.user.phone,
      auto_recharge_enabled: res.user.auto_recharge_enabled,
      primary_card: res.user.primary_card,
      backup_card: res.user.backup_card,
      recharge_low_limit: res.user.recharge_low_limit,
      recharge_amount: res.user.recharge_amount,
      // id de corporate: _id
      corporate_id: res.user._id,
      // user id: user_id
      user_id: res.user?.user_id,
      tripStopsLimit: res.user?.tripStopsLimit,
      payments_dni: res.user?.payments_dni,
      overdraft_amount: res.user.overdraft_amount
        ? res.user.overdraft_amount
        : 0,
      enable_dev_module: res.user.enable_dev_module
        ? res.user.enable_dev_module
        : false,
      // Un corporativo hijo nunca ve el módulo de desarrollo. `parent_corporate`
      // solo viene cuando es hijo: en uno raíz la clave falta, nunca es `null`,
      // así que se lee por presencia y no comparando con `null`.
      is_child_corporate: Boolean(res.user?.parent_corporate),
      enable_prime_module: res.user.enable_prime_module
        ? res.user.enable_prime_module
        : false,
      hasShipmentModuleActive: res.user?.hasShipmentModuleActive || false,
      enabled_verticals: res.user?.enabled_verticals || [],
      max_trips_search_days_range: res.user?.max_trips_search_days_range || 0,
      // Paneles a los que el usuario tiene acceso. Los trae `get-profile`, que
      // ya se pide en cada montaje y después de cada `startSession`, así que no
      // hace falta `my-panels`: es la misma data sin un round-trip extra.
      //
      // ⚠️ `undefined` y `[]` NO son lo mismo acá. `undefined` significa «esta
      // respuesta no reporta paneles» —el login y `switch-panel` puede que no los
      // traigan— y entonces se conserva la lista que ya había; `[]` significa
      // «reportados: ninguno» (sesión legacy) y ahí sí se vacía. Sin esta
      // distinción el selector del menú parpadea al cambiar de panel.
      panels: res.user?.panels
        ? res.user.panels.map(mapEligiblePanelApiToEligiblePanel)
        : datalogin?.panels ?? [],
      is_owner: res.user?.loggedUser?.is_owner,
      permissions: parsePermissionSlugs(
        res.user?.loggedUser?.permissions ?? [],
      ),
      user_logged: res.user?.loggedUser,
      requires_session_renewal: res?.requiresSessionRenewal,
      // Estado de verificación del panel (CORP-592). `null` = el backend no lo
      // reporta (corporativo sin migrar): se mantiene la regla de `is_approved`.
      verification_status: res.user?.verification_status ?? null,
      allow_operations: res.user?.allow_operations ?? false,
      verification_mode: res.user?.verification_mode ?? null,
      // Solo lo traen los paneles creados por el registro v2 (CORP-592).
      panel_type: res.user?.panel_type ?? null,
      // Último estado del trámite en Legal (CORP-598); ausente sin trámite.
      // Solo `get-profile` lo trae: login/activate/switch conservan el del mismo panel.
      legal_verification_status: resolveLegalVerificationStatus(
        res.user?.legal_verification_status,
        {
          corporateId: datalogin?.corporate_id,
          status: datalogin?.legal_verification_status,
        },
        res.user._id,
      ),
    };

    if (
      datalogin?.corporate_id &&
      datalogin.corporate_id !== dataLogin.corporate_id
    ) {
      queryClient.clear();
    }

    localStorage.setItem('dataLogin', JSON.stringify(dataLogin));
    setDataLogin(dataLogin);
    setIsProfileValidated(true);
  };

  const _isAuth = (fromLogin = false) => {
    let dataLocal = JSON.parse(localStorage.getItem('dataLogin'));
    if (dataLocal) {
      setDataLogin(dataLocal);
      getUserData(dataLocal.route).then();

      // Un enlace directo a una ruta específica se respeta tal cual — no se
      // pisa con la última sección guardada en `dataLogin.route`.
      //
      // Antes esto navegaba SIEMPRE a `dataLocal.route`, en cada carga dura,
      // sin mirar a dónde había navegado el usuario. Pegar
      // `/trips?tab=viajes` en la barra de direcciones terminaba en
      // `/prime?tab=viajes` —la última sección que se había usado antes—
      // porque `searchParams` sí reflejaba la URL real (la query sobrevive)
      // mientras que `dataLocal.route` no tiene forma de saber cuál era el
      // path pedido (se pierde). El fallback a la última sección solo tiene
      // sentido cuando se entra por un punto de entrada genérico —la raíz o
      // `/select-panel`—, no cuando el usuario ya trae un destino concreto.
      //
      // `/select-panel` cuenta como genérico: `startSession` llama a
      // `_isAuth()` justo después de activar un panel (CORP-546), y en ese
      // momento el pathname todavía es `/select-panel`. Sin incluirlo acá la
      // navegación a `home` nunca se disparaba y la pantalla se quedaba en
      // blanco tras elegir panel.
      //
      // `/login` es genérico SOLO cuando `_isAuth` se llama recién autenticado
      // (`fromLogin`, vía `startSession`) — justo después de enviar el
      // formulario el pathname sigue siendo `/login`, y ahí sí hay que navegar.
      // El destino es `home`: `startSession` llama antes a `handleUserData`,
      // que guarda `route = 'home'`, y de todas formas no hay sección previa
      // que conservar porque logout y los 401/403 hacen `localStorage.clear()`.
      // Igual que en `main`. En el bootstrap de la app (carga dura, sesión ya
      // válida) `fromLogin` es `false`: si el pathname pedido es `/login`, ya
      // no cuenta como genérico, así que `_isAuth` no navega y deja que
      // `RouterPrivate` lo resuelva — no tiene `Route` para `/login`, así que
      // cae en el wildcard a `/home`. Antes de esto, un usuario ya logueado
      // que escribía `/login` en la barra terminaba en la última sección
      // visitada en vez de Home (CORP-548).
      const isGenericEntryPoint =
        location.pathname === '/' ||
        location.pathname === '/select-panel' ||
        (fromLogin && location.pathname === '/login');
      if (!isGenericEntryPoint) return;

      dataLocal?.route
        ? navigate(`${dataLocal?.route}?${searchParams.toString()}`)
        : navigate('home');
    }
  };

  // Arranca la sesión a partir de una respuesta de autenticación exitosa. Las
  // tres la comparten —login de un solo panel, `activate-panel` y, en el
  // futuro, `switch-panel`—, así que todas entran por el mismo camino.
  const startSession = (res) => {
    // Que arranque una sesión cierra cualquier login a medio terminar, sea el que
    // acaba de resolverse o uno anterior que el usuario abandonó. Si no, el token
    // temporal de esa otra cuenta seguiría en `sessionStorage`, todavía utilizable.
    clearPendingLogin();
    window.localStorage.setItem('token', res.tokenJWT);
    handleUserData(res);
    _isAuth(true);
  };

  const signIn = async (data) => {
    try {
      setLoading(true);
      const res = await RequestLogin(data);

      // Caso multi-panel (CORP-546): el usuario pertenece a 2+ paneles activos,
      // así que todavía no hay sesión. El backend manda `panels` y un
      // `panelSelectionToken` de 5 minutos en lugar de `tokenJWT`, y hay que
      // preguntarle a dónde quiere entrar antes de poder autenticarlo.
      // Sin `panelSelectionToken` no hay login pendiente que guardar:
      // escribirlo igual dejaría la cadena "undefined" en sessionStorage, que
      // `readPendingLogin` daría por válida y terminaría en un 401 mudo al
      // elegir panel.
      if (!res?.tokenJWT && res?.panels?.length && res?.panelSelectionToken) {
        savePendingLogin({
          token: res.panelSelectionToken,
          panels: res.panels.map(mapEligiblePanelApiToEligiblePanel),
          email: data?.email ?? '',
        });
        navigate('/select-panel');
        return;
      }

      if (!res?.user) {
        return;
      }
      startSession(res);
    } catch (e) {
      // Panel suspendido (CORP-592): lo muestra el formulario en línea, con el
      // mensaje del backend, en vez de una alerta modal.
      if (e?.error_code === SUSPENDED_PANEL_ERROR_CODE) throw e;
      errorAlert(
        '',
        e.message ||
          '¡Disculpe ha ocurrido un error inesperado, por favor intente mas tarde!.',
      );
    } finally {
      setLoading(false);
    }
  };
  const clearStorage = () => {
    localStorage.clear();
  };
  const logout = () => {
    clearStorage();
    // `clearStorage` solo vacía `localStorage`; el login pendiente vive en
    // `sessionStorage` y sobreviviría al cierre de sesión.
    clearPendingLogin();
    resetCleverTapIdentity();
    queryClient.clear();
    // Kustomer se detiene reactivamente en KustomerBootstrap al quedar datalogin
    // en null (oculta el bubble y cierra la sesión de chat).
    setDataLogin(null);
    setIsProfileValidated(false);
    navigate('login');
  };
  const requestLogout = () => {
    sweetalertLogout('¿Quieres cerrar sesión?', '', logout);
  };

  const confirmeRemoveUser = async (data, hide, refreshData) => {
    try {
      const confirm = await sweetalertConfirm(
        '',
        `¿Estás seguro que deseas remover a ${data.full_name} ?`,
        'warning',
      );
      if (confirm) {
        await RemoveUser({
          userId: data._id,
        });

        successAlert(
          '',
          `El usuario ${data.full_name} fue removido exitosamente!`,
        );
        refreshData();
        hide();
      }
    } catch (e) {
      errorAlert(
        '',
        e.message ||
          '¡Disculpe ha ocurrido un error inesperado, por favor intente mas tarde!.',
      );
    }
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        datalogin,
        setDataLogin,
        isProfileValidated,
        refreshProfile,
        loading,
        setLoading,
        signIn,
        startSession,
        requestLogout,
        confirmeRemoveUser,
        showBanner,
        setShowBanner,
      }}
    >
      {children}
      <SessionRenewalModal
        isOpen={!!datalogin?.requires_session_renewal}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};
export default AuthContext;
